'use client';

import { conversationService } from '@/api/services/conversationService';
import { QUERY_KEYS } from '@/constants';
import { useAuthContext } from '@/contexts/auth';
import { useOpenComment } from '@/contexts/comment';
import { usePopupConfirmContext } from '@/contexts/popup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import type { IReaction } from '../types';

export const useCommentActions = (conversationId: string) => {
	const queryClient = useQueryClient();

	const { onClose } = useOpenComment();

	const { isGuest } = useAuthContext();
	const router = useRouter();
	const { show } = usePopupConfirmContext();
	const { t } = useTranslation('common');

	const invalidateAll = (commentId?: number) => {
		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FOR_YOU_TRENDING] });
		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FOR_YOU_FOLLOW_UP] });

		queryClient.invalidateQueries({
			queryKey: [QUERY_KEYS.CONVERSATION_COMMENTS, conversationId],
		});
		if (commentId !== undefined) {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.CONVERSATION_COMMENT_REPLIES, commentId],
			});
		}
	};

	const createCommentMutation = useMutation({
		mutationFn: (content: string) => {
			if (isGuest) {
				show({
					message: t('ForbiddenException: Please sign in to create comment'),
					onConfirm: () => {
						onClose();
						router.push('/my_profile/login');
					},
				});
				return Promise.reject(new Error('Guest user cannot create comment'));
			}
			return conversationService.addComment(conversationId, { content });
		},
		onSuccess: () => {
			invalidateAll();
		},
	});

	const likeCommentMutation = useMutation({
		mutationFn: ({
			commentId,
			data,
		}: { commentId: number; data: IReaction }) => {
			if (isGuest) {
				show({
					message: t('ForbiddenException: Please sign in to like comment'),
					onConfirm: () => {
						onClose();
						router.push('/my_profile/login');
					},
				});
				return Promise.reject(new Error('Guest user cannot like comment'));
			}
			return conversationService.reactToComment(commentId, data);
		},
		onSuccess: (_data, variables) => {
			invalidateAll(variables.commentId);
		},
	});

	const replyToCommentMutation = useMutation({
		mutationFn: ({
			commentId,
			content,
		}: { commentId: number; content: string }) => {
			if (isGuest) {
				show({
					message: t('ForbiddenException: Please sign in to reply to comment'),
					onConfirm: () => {
						onClose();
						router.push('/my_profile/login');
					},
				});
				return Promise.reject(new Error('Guest user cannot reply to comment'));
			}
			return conversationService.addComment(conversationId, {
				content,
				parentCommentId: commentId,
			});
		},
		onSuccess: (_data, variables) => {
			invalidateAll(variables.commentId);
		},
	});

	return {
		createComment: createCommentMutation.mutate,
		likeComment: likeCommentMutation.mutate,
		replyToComment: replyToCommentMutation.mutate,
	};
};
